import { useContext } from 'react';

import { ThemeContext } from './ThemeContext.jsx';
import { STORY_MODES, RELATIONSHIP_BASELINES } from './StorySetupPanel.jsx';
import ThreadRouteSelect from './ThreadRouteSelect.jsx';
import { routeOptionsFor } from './archiveLore.js';
// ─── Story Preferences Panel ──────────────────────────────────────────────
// Per-conversation / per-story settings that shape AI character behavior.
// Saved to dm_conversations.story_preferences or stories.story_preferences (JSONB).

export const STORY_PREF_OPTIONS = {
  romancePacing: {
    label: "Romance Pacing",
    description: "How quickly romantic feelings develop",
    options: [
      { value: "", label: "Let the story decide" },
      { value: "glacier", label: "Glacier — every glance matters, painfully slow" },
      { value: "slow_burn", label: "Slow Burn — tension builds over many exchanges" },
      { value: "moderate", label: "Moderate — natural progression, not rushed" },
      { value: "fast", label: "Fast — feelings develop quickly, high chemistry" },
      { value: "instant", label: "Instant — sparks fly immediately" },
    ],
  },
  // relationshipStage purged (queue item 16, 2026-08-04): the stage enum was
  // superseded by typed relationship kinds in character_dynamics (edited via
  // the Relationships panel). romancePacing/intimacyComfort remain — only
  // the stage was replaced. Values were migrated to rows before removal.
  intimacyComfort: {
    label: "Intimacy Comfort Level",
    description: "How far physical/emotional intimacy can go",
    options: [
      { value: "", label: "Let the story decide" },
      { value: "fade_to_black", label: "Fade to Black — imply, never describe" },
      { value: "tasteful", label: "Tasteful — light touches, kisses, emotional closeness" },
      { value: "moderate", label: "Moderate — romantic scenes with some detail" },
      { value: "descriptive", label: "Descriptive — detailed romantic/intimate scenes" },
      { value: "platonic_only", label: "Platonic Only — no romance, friendship or rivalry focus" },
    ],
  },
  conflictStyle: {
    label: "Conflict Style",
    description: "How much tension and drama the character brings",
    options: [
      { value: "", label: "Let the story decide" },
      { value: "gentle", label: "Gentle — disagreements are soft, resolved quickly" },
      { value: "realistic", label: "Realistic — natural friction, miscommunication happens" },
      { value: "dramatic", label: "Dramatic — heated arguments, strong emotions" },
      { value: "angsty", label: "Angsty — deep emotional pain, inner turmoil, heartache" },
      { value: "chaotic", label: "Chaotic — unpredictable, volatile, high-stakes clashes" },
      { value: "passive_aggressive", label: "Passive-Aggressive — subtle digs, unspoken issues" },
      { value: "none", label: "No Conflict — keep things smooth and harmonious" },
    ],
  },
  characterAttachment: {
    label: "Character Attachment",
    description: "How emotionally invested the character is in your character",
    options: [
      { value: "", label: "Let the story decide" },
      { value: "guarded", label: "Guarded — walls up, slow to trust" },
      { value: "cautious", label: "Cautious — interested but careful" },
      { value: "warming", label: "Warming Up — starting to care, showing small signs" },
      { value: "attached", label: "Attached — clearly cares, invested in the relationship" },
      { value: "devoted", label: "Devoted — deeply bonded, protective, loyal" },
      { value: "obsessive", label: "Obsessive — intensely fixated, can't let go" },
      { value: "indifferent", label: "Indifferent — emotionally detached, hard to read" },
    ],
  },
  storyTone: {
    label: "Story Tone",
    description: "The overall mood and feel of this conversation",
    options: [
      { value: "", label: "Let the story decide" },
      { value: "lighthearted", label: "Lighthearted — fun, playful, low stakes" },
      { value: "cozy", label: "Cozy — warm, comforting, slice-of-life" },
      { value: "romantic", label: "Romantic — love-focused, tender moments" },
      { value: "dramatic", label: "Dramatic — emotional highs and lows, tension" },
      { value: "dark", label: "Dark — heavy themes, moral complexity, grit" },
      { value: "mysterious", label: "Mysterious — secrets, suspense, intrigue" },
      { value: "humorous", label: "Humorous — witty banter, comedy, lighthearted chaos" },
      { value: "bittersweet", label: "Bittersweet — beautiful and painful in equal measure" },
      { value: "action", label: "Action — fast-paced, danger, adventure" },
    ],
  },
};

// Default empty prefs
export const DEFAULT_STORY_PREFS = {
  // Story Frame (new)
  story_mode: null,
  relationship_baseline: null,
  cowriter_context_note: '',
  canon_route: '', // this thread's played route (2026-10-12); '' = the map's canon routes
  // DM Timeline connection
  dmContextMode: 'linked',
  // Story Preferences (existing)
  romancePacing: "",
  intimacyComfort: "",
  conflictStyle: "",
  characterAttachment: "",
  storyTone: "",
};

// Build a prompt section from active preferences
export function buildStoryPrefsPrompt(prefs, charName = "the character") {
  if (!prefs) return "";

  const lines = [];

  // Romance pacing
  if (prefs.romancePacing) {
    const opt = STORY_PREF_OPTIONS.romancePacing.options.find(o => o.value === prefs.romancePacing);
    if (opt) lines.push(`ROMANCE PACING: ${opt.label}. Match this speed for any romantic development.`);
  }

  // Intimacy comfort
  if (prefs.intimacyComfort) {
    const opt = STORY_PREF_OPTIONS.intimacyComfort.options.find(o => o.value === prefs.intimacyComfort);
    if (opt) lines.push(`INTIMACY LEVEL: ${opt.label}. Never exceed this boundary.`);
  }

  // Conflict style
  if (prefs.conflictStyle) {
    const opt = STORY_PREF_OPTIONS.conflictStyle.options.find(o => o.value === prefs.conflictStyle);
    if (opt) lines.push(`CONFLICT STYLE: ${opt.label}.`);
  }

  // Character attachment
  if (prefs.characterAttachment) {
    const opt = STORY_PREF_OPTIONS.characterAttachment.options.find(o => o.value === prefs.characterAttachment);
    if (opt) lines.push(`${charName.toUpperCase()}'S ATTACHMENT: ${opt.label}.`);
  }

  // Story tone
  if (prefs.storyTone) {
    const opt = STORY_PREF_OPTIONS.storyTone.options.find(o => o.value === prefs.storyTone);
    if (opt) lines.push(`STORY TONE: ${opt.label}. Let this mood guide your writing.`);
  }

  // THIS THREAD'S ROUTE (2026-10-12): the thread continues one played route;
  // it outranks every other route and any profile line about their history.
  if (typeof prefs.canon_route === 'string' && prefs.canon_route.trim()) {
    const route = prefs.canon_route.trim();
    lines.push(`THIS THREAD'S ROUTE: #${route}. This story continues the one played there. Where any other route, archive passage or profile line disagrees with that route about what happened between you (how you met, your first night, who you were to each other), that route is the truth and the rest did not happen here.`);
  }

  if (lines.length === 0) return "";

  return `\nSTORY PREFERENCES (user-set — respect these):\n${lines.join("\n")}`;
}

// ─── Panel Component ──────────────────────────────────────────────────────
// `embedded` renders just the settings BODY, no overlay/fixed shell — how the
// panel lives inside ProfileSettings' Story tab (queue item 17, "ONE menu").
export default function StoryPreferencesPanel({ prefs, onChange, onClose, characterName, characterId = null, embedded = false }) {
  const { theme: t } = useContext(ThemeContext);
  const currentPrefs = { ...DEFAULT_STORY_PREFS, ...prefs };

  const handleChange = (key, value) => {
    const updated = { ...currentPrefs, [key]: value };
    onChange(updated);
  };

  const panelStyle = {
    position: "fixed", top: 0, right: 0, width: "min(360px, 90vw)", height: "100%",
    background: t.bgBase, borderLeft: "1px solid var(--ds-accent-a44, #c6282844)", zIndex: 1000,
    display: "flex", flexDirection: "column", color: t.textPrimary,
    boxShadow: "-4px 0 20px rgba(0,0,0,0.5)", animation: "slideInRight 0.2s ease-out",
  };

  const sectionStyle = { marginBottom: "18px" };

  const labelStyle = {
    fontSize: "12px", color: t.accentGold, textTransform: "uppercase", letterSpacing: "1px",
    marginBottom: "4px", display: "block", fontFamily: "'Cinzel', serif",
  };

  const descStyle = {
    fontSize: "11px", color: t.textMuted, marginBottom: "8px", display: "block", lineHeight: 1.4,
  };

  const selectStyle = {
    width: "100%", padding: "10px 12px", borderRadius: "8px",
    border: "1px solid var(--ds-accent-a33, #c6282833)", background: "#0d0a0a", color: t.textPrimary,
    fontSize: "13px", cursor: "pointer", outline: "none",
    fontFamily: "'Inter', system-ui, sans-serif",
  };

  // Check if any prefs are set
  const hasAnyPrefs = Object.values(currentPrefs).some(v => v !== "");

  const body = (
    <>
          <p style={{ fontSize: "12px", color: t.textMuted, margin: "0 0 20px", lineHeight: 1.5 }}>
            Shape how {characterName || "the character"} behaves in this conversation. Leave any setting on "Let the story decide" to keep it natural.
          </p>

          {/* ── Story Frame (story mode + baseline + context note) ──────── */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ ...labelStyle, fontSize: '11px' }}>Story Frame</label>
            <span style={descStyle}>How the character enters this story — fresh world or continuing canon.</span>

            <label style={{ ...labelStyle, fontSize: '10px', marginTop: '10px' }}>Story Mode</label>
            <select
              value={currentPrefs.story_mode || 'alternate_route'}
              onChange={e => handleChange('story_mode', e.target.value)}
              style={selectStyle}
            >
              {STORY_MODES.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>

            <label style={{ ...labelStyle, fontSize: '10px', marginTop: '10px' }}>Relationship Baseline</label>
            <select
              value={currentPrefs.relationship_baseline || 'strangers'}
              onChange={e => handleChange('relationship_baseline', e.target.value)}
              style={selectStyle}
            >
              {RELATIONSHIP_BASELINES.map(b => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>

            {characterId && routeOptionsFor(characterId).length > 0 && (
              <>
                <label style={{ ...labelStyle, fontSize: '10px', marginTop: '10px' }}>This Thread's Route</label>
                <ThreadRouteSelect
                  characterId={characterId}
                  value={currentPrefs.canon_route || ''}
                  onChange={v => handleChange('canon_route', v)}
                  selectStyle={selectStyle}
                  hintStyle={{ ...descStyle, marginTop: '6px' }}
                />
              </>
            )}

            <label style={{ ...labelStyle, fontSize: '10px', marginTop: '10px' }}>Context Note</label>
            <textarea
              value={currentPrefs.cowriter_context_note || ''}
              onChange={e => handleChange('cowriter_context_note', e.target.value)}
              placeholder="Scenario, tone, who your character is in this story..."
              style={{ ...selectStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <div style={{ borderTop: '1px solid var(--ds-accent-a20, #c6282820)', marginBottom: '20px' }} />

          {/* ── DM Timeline Connection ──────────────────────────────────── */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ ...labelStyle, fontSize: '11px' }}>DM Timeline Connection</label>
            <span style={descStyle}>Controls whether this RP draws from your DM history with this character. Use this when running a different route or alternate universe.</span>
            {[
              { value: 'linked',   label: '🔗 Linked',   desc: 'DM history informs this RP (default)' },
              { value: 'isolated', label: '✂️ Isolated',  desc: 'Separate timeline — ignore DM context' },
              { value: 'prequel',  label: '⏮ Prequel',   desc: 'This RP happens before the DM events' },
            ].map(opt => {
              const isActive = (currentPrefs.dmContextMode || 'linked') === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleChange('dmContextMode', opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '10px 12px', marginBottom: '6px', borderRadius: '8px',
                    background: isActive ? 'var(--ds-gold-a18, #c4a88218)' : 'transparent',
                    border: `1px solid ${isActive ? 'var(--ds-gold-a66, #c4a88266)' : 'var(--ds-accent-a20, #c6282820)'}`,
                    color: isActive ? 'var(--ds-gold, #c4a882)' : '#888', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '13px', minWidth: '90px', color: isActive ? 'var(--ds-gold, #c4a882)' : '#aaa' }}>{opt.label}</span>
                  <span style={{ fontSize: '11px', color: isActive ? 'var(--ds-gold-a99, #c4a88299)' : '#555' }}>{opt.desc}</span>
                </button>
              );
            })}
          </div>

          <div style={{ borderTop: '1px solid var(--ds-accent-a20, #c6282820)', marginBottom: '20px' }} />

          {Object.entries(STORY_PREF_OPTIONS).map(([key, config]) => (
            <div key={key} style={sectionStyle}>
              <label style={labelStyle}>{config.label}</label>
              <span style={descStyle}>{config.description}</span>
              <select
                value={currentPrefs[key] || ""}
                onChange={(e) => handleChange(key, e.target.value)}
                style={{
                  ...selectStyle,
                  borderColor: currentPrefs[key] ? "#c4a88855" : "var(--ds-accent-a33, #c6282833)",
                }}
              >
                {config.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}

          {/* Reset button */}
          {hasAnyPrefs && (
            <button
              onClick={() => onChange({ ...DEFAULT_STORY_PREFS })}
              style={{
                width: "100%", padding: "10px", borderRadius: "8px",
                border: "1px solid var(--ds-accent-a44, #c6282844)", background: "transparent",
                color: t.accentDanger, fontSize: "12px", cursor: "pointer",
                fontFamily: "'Cinzel', serif", letterSpacing: "1px",
                marginTop: "8px", transition: "all 0.2s",
              }}
            >
              Reset All to Default
            </button>
          )}
    </>
  );

  // Embedded: just the body — the host (ProfileSettings' Story tab) owns
  // chrome, scrolling, and dismissal.
  if (embedded) return <div>{body}</div>;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999 }} />
      <div style={panelStyle}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid var(--ds-accent-a33, #c6282833)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "15px", letterSpacing: "2px", color: t.accentGold }}>
              Story Preferences
            </span>
            {characterName && (
              <div style={{ fontSize: "11px", color: t.textMuted, marginTop: "2px" }}>
                with {characterName}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.textMuted, fontSize: "18px", cursor: "pointer" }}>✕</button>
        </div>

        {/* Settings */}
        <div style={{ padding: "20px", overflowY: "auto", flex: 1 }}>
          {body}
        </div>
      </div>
    </>
  );
}
